// Firebase is loaded lazily (dynamic import) so the SDK never enters the eager
// bundle — it only downloads when the user actually clicks "Đăng nhập với Google".
import type { FirebaseApp } from "firebase/app";

// Các giá trị Firebase Web đều là public (không phải secret), nên giữ sẵn bản
// dự phòng trong code để app không vỡ khi file .env bị reset.
const FIREBASE_FALLBACK = {
  apiKey: "",
  authDomain: "boi-app-4521b.firebaseapp.com",
  projectId: "boi-app-4521b",
  storageBucket: "boi-app-4521b.firebasestorage.app",
  messagingSenderId: "719274047174",
  appId: "1:719274047174:web:db513d04e05b8b77c689c7",
  measurementId: "G-Z8XPXTGLSE",
};

const env = (value: unknown, fallback: string): string | undefined => {
  const v = typeof value === "string" ? value.trim() : "";
  // Bỏ qua giá trị placeholder kiểu "@secret:NAME" chưa được thay thế.
  if (!v || v.startsWith("@secret:")) return fallback || undefined;
  return v;
};

const firebaseConfig = {
  apiKey: env(import.meta.env.VITE_FIREBASE_API_KEY, FIREBASE_FALLBACK.apiKey),
  authDomain: env(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, FIREBASE_FALLBACK.authDomain),
  projectId: env(import.meta.env.VITE_FIREBASE_PROJECT_ID, FIREBASE_FALLBACK.projectId),
  storageBucket: env(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET, FIREBASE_FALLBACK.storageBucket),
  messagingSenderId: env(
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    FIREBASE_FALLBACK.messagingSenderId,
  ),
  appId: env(import.meta.env.VITE_FIREBASE_APP_ID, FIREBASE_FALLBACK.appId),
  measurementId: env(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, FIREBASE_FALLBACK.measurementId),
};

let appPromise: Promise<FirebaseApp> | null = null;
// Giữ nguyên CHUỖI khoá cũ: đổi tên biến thôi, đổi cả giá trị là bỏ rơi những
// phiên đang dở dang giữa chừng một lần redirect.
const LOGIN_PENDING_KEY = "boide_google_login_pending";

/**
 * Ném ra khi trang đã rời sang nhà cung cấp OAuth — KHÔNG phải lỗi.
 * `getRedirectResult()` sẽ hoàn tất phần còn lại khi người dùng quay về.
 */
const REDIRECTING_MESSAGE = "Đang chuyển hướng đến trang đăng nhập...";

export function isRedirectingError(error: unknown): boolean {
  return error instanceof Error && error.message === REDIRECTING_MESSAGE;
}

/**
 * Nhà cung cấp của lượt redirect đang dở dang.
 * Bản cũ ghi "1" (chỉ có Google) — vẫn đọc được để không bỏ rơi phiên đang chạy.
 */
export function getPendingOAuthProvider(): OAuthProviderName | null {
  if (typeof window === "undefined") return null;
  const value = localStorage.getItem(LOGIN_PENDING_KEY);
  if (!value) return null;
  return value === "apple" ? "apple" : "google";
}

function firebaseErrorCode(error: unknown): string {
  if (typeof error !== "object" || error === null || !("code" in error)) return "";
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : "";
}

/** IP LAN riêng (RFC 1918) — máy dev truy cập từ điện thoại cùng WiFi. */
const PRIVATE_LAN_IP_PATTERN = /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    PRIVATE_LAN_IP_PATTERN.test(hostname)
  );
}

function runtimeAuthDomain(): string | undefined {
  if (typeof window === "undefined") return firebaseConfig.authDomain;
  const hostname = window.location.hostname;
  // Firebase redirect helpers must be served from the same site on modern Safari.
  // Local development keeps using the default firebaseapp.com helper — kể cả khi
  // mở qua IP LAN từ điện thoại, vì `window.location.host` lúc đó kèm cả port
  // (vd "192.168.1.4:8080") nên không bao giờ là authDomain hợp lệ.
  if (!isLocalHostname(hostname)) return window.location.host;
  return firebaseConfig.authDomain;
}

function shouldUseRedirect(): boolean {
  if (typeof window === "undefined") return false;
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  // Popup OAuth trên iOS (Safari, Chrome iOS và Safari View Controller) có thể
  // bị chặn hoặc trả về trang lỗi ở lần mở đầu. Redirect cùng tab ổn định hơn.
  return ios;
}

async function getApp(): Promise<FirebaseApp> {
  if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId) {
    throw new Error("Thiếu cấu hình Firebase Auth.");
  }
  if (!appPromise) {
    appPromise = import("firebase/app").then(({ initializeApp }) =>
      initializeApp({ ...firebaseConfig, authDomain: runtimeAuthDomain() }),
    );
  }
  return appPromise;
}

function isInAppBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // Detect in-app browsers: Facebook, Instagram, Messenger, TikTok, Line, Zalo, WeChat
  return /FBAN|FBAV|Instagram|MessengerLite|TikTok|Line|Zalo|MicroMessenger|WeChat/i.test(ua);
}

export async function getRedirectResult(): Promise<string | null> {
  try {
    const [app, { getAuth, getRedirectResult: fbGetRedirectResult }] = await Promise.all([
      getApp(),
      import("firebase/auth"),
    ]);
    const auth = getAuth(app);
    const result = await fbGetRedirectResult(auth);
    if (result?.user) {
      localStorage.removeItem(LOGIN_PENDING_KEY);
      return result.user.getIdToken();
    }
    await auth.authStateReady();
    if (getPendingOAuthProvider() && auth.currentUser) {
      localStorage.removeItem(LOGIN_PENDING_KEY);
      return auth.currentUser.getIdToken(true);
    }
  } catch (err) {
    // Handle redirect errors (auth/cross-origin-redirect-in-progress, etc.)
    const errorCode = firebaseErrorCode(err);
    if (errorCode === "auth/cross-origin-redirect-in-progress") {
      throw new Error("Quá trình đăng nhập đang diễn ra trên tab khác.");
    }
  }
  return null;
}

/** Clear the local Firebase Auth session without blocking the main logout flow. */
export async function signOutFirebase(): Promise<void> {
  if (!appPromise) return;
  try {
    const [app, { getAuth, signOut }] = await Promise.all([getApp(), import("firebase/auth")]);
    await signOut(getAuth(app));
  } catch {
    // Best-effort: a Firebase failure must not prevent the Boide session from ending.
  }
}

/** Nhà cung cấp OAuth được hỗ trợ. Dùng để dựng đúng provider Firebase. */
export type OAuthProviderName = "google" | "apple";

const APPLE_PROVIDER_ID = "apple.com";

type FirebaseAuthModule = typeof import("firebase/auth");

function buildProvider(name: OAuthProviderName, authModule: FirebaseAuthModule) {
  if (name === "apple") {
    const provider = new authModule.OAuthProvider(APPLE_PROVIDER_ID);
    // Apple chỉ gửi email/tên khi được xin rõ ràng, và CHỈ ở lần đồng ý đầu tiên.
    provider.addScope("email");
    provider.addScope("name");
    return provider;
  }
  return new authModule.GoogleAuthProvider();
}

async function signInWithOAuthProvider(name: OAuthProviderName): Promise<string> {
  const [app, authModule] = await Promise.all([getApp(), import("firebase/auth")]);
  const { browserLocalPersistence, getAuth, setPersistence, signInWithPopup, signInWithRedirect } =
    authModule;
  const auth = getAuth(app);
  const provider = buildProvider(name, authModule);

  // Chờ Firebase khôi phục session trước khi mở OAuth, tránh lần click đầu
  // chạy đua với quá trình khởi tạo persistence trên trình duyệt mới mở.
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch {
    // Một số chế độ riêng tư chặn persistence; Firebase vẫn có thể đăng nhập
    // bằng session tạm thời nên không chặn toàn bộ luồng.
  }
  await auth.authStateReady();
  if (auth.currentUser) {
    localStorage.removeItem(LOGIN_PENDING_KEY);
    return auth.currentUser.getIdToken(true);
  }

  localStorage.setItem(LOGIN_PENDING_KEY, name);

  // iOS dùng redirect ngay từ đầu; popup OAuth trên iOS dễ bị hệ điều hành
  // đóng ở lần đầu, khiến user phải bấm lại.
  if (shouldUseRedirect() || isInAppBrowser()) {
    try {
      await signInWithRedirect(auth, provider);
    } catch (error) {
      localStorage.removeItem(LOGIN_PENDING_KEY);
      throw error;
    }
    throw new Error(REDIRECTING_MESSAGE);
  }

  try {
    const result = await signInWithPopup(auth, provider);
    localStorage.removeItem(LOGIN_PENDING_KEY);
    return result.user.getIdToken(true);
  } catch (err) {
    const errorCode = firebaseErrorCode(err);
    if (
      errorCode !== "auth/popup-blocked" &&
      errorCode !== "auth/operation-not-supported-in-this-environment" &&
      errorCode !== "auth/cancelled-popup-request"
    ) {
      localStorage.removeItem(LOGIN_PENDING_KEY);
      throw err;
    }
  }

  // Popup bị chặn thì thử lại bằng redirect và hoàn tất qua getRedirectResult.
  try {
    await signInWithRedirect(auth, provider);
  } catch (error) {
    localStorage.removeItem(LOGIN_PENDING_KEY);
    throw error;
  }
  throw new Error(REDIRECTING_MESSAGE);
}

export async function signInWithGoogle(): Promise<string> {
  return signInWithOAuthProvider("google");
}

export async function signInWithApple(): Promise<string> {
  return signInWithOAuthProvider("apple");
}

/**
 * Dịch lỗi Firebase Auth sang câu người dùng đọc được.
 *
 * Trả `null` khi không nhận ra mã lỗi, để call site tự lo phần còn lại.
 */
export function firebaseAuthErrorMessage(error: unknown): string | null {
  const code = firebaseErrorCode(error);

  // Cùng một email nhưng Firebase đang để chế độ "mỗi email một tài khoản":
  // người đã đăng ký bằng Google không đăng nhập Apple được, và ngược lại.
  if (code === "auth/account-exists-with-different-credential") {
    return "Email này đã được đăng ký bằng cách đăng nhập khác. Vui lòng dùng đúng cách đăng nhập trước đây của bạn.";
  }
  if (code === "auth/invalid-credential") {
    return "Thông tin đăng nhập không hợp lệ hoặc đã hết hạn. Vui lòng thử lại.";
  }
  if (code === "auth/network-request-failed") {
    return "Không kết nối được máy chủ đăng nhập. Kiểm tra lại mạng rồi thử lại.";
  }
  return null;
}

// ============================================================
// Link trong email (oobCode): đặt lại mật khẩu & xác thực email
// ============================================================
//
// 🔴 Ở LUỒNG NÀY FIREBASE ĐƯỢC GỌI TRỰC TIẾP, KHÔNG QUA BE — và đó là chủ ý, không
// phải thiếu sót. `oobCode` trong link là bằng chứng do chính Firebase phát hành và
// chỉ Firebase mới xác nhận được; BE có sẵn `confirm_password_reset()` nhưng KHÔNG
// endpoint nào gọi tới, nên bắc thêm một chặng qua BE chỉ để chuyển tiếp nguyên văn
// oobCode là thêm một chỗ hỏng mà không thêm một phép kiểm nào.
//
// ⚠️ Mật khẩu mới đặt ở đây KHÔNG ghi vào `users.hashed_password` của Postgres. Vẫn
// đăng nhập được vì `verify_login_password` ưu tiên Firebase khi tài khoản đã có
// `firebase_uid` — chỉ tài khoản CŨ chưa gắn Firebase mới rơi về bcrypt, và những
// tài khoản đó cũng không nhận được mail đặt lại (xem `ctr_forgot_password`).
//
// ⚠️ Route `/dat-lai-mat-khau` chỉ nhận được oobCode khi Firebase Console được trỏ
// "Action URL" về chính nó (Authentication → Templates → sửa từng mẫu email). Chưa
// trỏ thì link trong mail vẫn đi tới trang mặc định của Firebase và trang này không
// bao giờ được mở — hỏng trong im lặng, không có lỗi nào để bắt.

/** Chế độ do Firebase gắn vào link (`?mode=`). Một Action URL phục vụ mọi chế độ. */
export type FirebaseActionMode = "resetPassword" | "verifyEmail" | "recoverEmail" | "unknown";

export function parseFirebaseActionMode(mode: unknown): FirebaseActionMode {
  if (mode === "resetPassword" || mode === "verifyEmail" || mode === "recoverEmail") return mode;
  return "unknown";
}

/**
 * Kiểm oobCode còn hiệu lực và cho biết nó thuộc email nào.
 *
 * Gọi TRƯỚC khi hiện form đặt mật khẩu: link hết hạn là chuyện rất thường (Firebase
 * cho ~1 giờ, và mọi lần bấm "quên mật khẩu" lại vô hiệu hoá link cũ). Biết sớm thì
 * báo "link đã hết hạn" ngay, thay vì để người dùng gõ xong mật khẩu mới rồi mới đổ.
 */
export async function verifyPasswordResetCode(oobCode: string): Promise<string> {
  const [app, { getAuth, verifyPasswordResetCode: fbVerify }] = await Promise.all([
    getApp(),
    import("firebase/auth"),
  ]);
  return fbVerify(getAuth(app), oobCode);
}

/** Đặt mật khẩu mới bằng oobCode. Sau bước này oobCode chết, không dùng lại được. */
export async function confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
  const [app, { getAuth, confirmPasswordReset: fbConfirm }] = await Promise.all([
    getApp(),
    import("firebase/auth"),
  ]);
  await fbConfirm(getAuth(app), oobCode, newPassword);
}

/**
 * Xác nhận email (mode=verifyEmail) / khôi phục email (mode=recoverEmail).
 *
 * 🔴 Phải có mặt ở đây dù màn hình mang tên "đặt lại mật khẩu": Action URL tuỳ biến
 * áp cho MẪU EMAIL nào được trỏ tới nó, và mail xác thực tài khoản cũng đi qua cùng
 * một cửa. Thiếu nhánh này thì người mới đăng ký bấm link xác thực sẽ rơi vào một
 * trang báo "link không hợp lệ" — mà tài khoản của họ thì vẫn chưa được kích hoạt.
 */
export async function applyFirebaseActionCode(oobCode: string): Promise<void> {
  const [app, { getAuth, applyActionCode }] = await Promise.all([
    getApp(),
    import("firebase/auth"),
  ]);
  await applyActionCode(getAuth(app), oobCode);
}

/**
 * Câu lỗi cho riêng luồng oobCode.
 *
 * Tách khỏi `firebaseAuthErrorMessage` vì cùng một mã lỗi đọc khác nhau ở hai chỗ:
 * `auth/invalid-action-code` ở đây nghĩa là "link đã dùng rồi hoặc đã bị thay bằng
 * link mới hơn", một câu vô nghĩa ở màn đăng nhập.
 */
export function firebaseActionErrorMessage(error: unknown): string {
  const code = firebaseErrorCode(error);
  if (code === "auth/expired-action-code") {
    return 'Link đã hết hạn. Hãy bấm "Quên mật khẩu" để nhận link mới.';
  }
  if (code === "auth/invalid-action-code") {
    return "Link không còn hiệu lực — có thể bạn đã dùng nó rồi, hoặc đã yêu cầu một link mới hơn. Hãy lấy lại link mới.";
  }
  if (code === "auth/user-disabled") return "Tài khoản này đang bị khoá.";
  if (code === "auth/user-not-found") return "Không tìm thấy tài khoản ứng với link này.";
  if (code === "auth/weak-password")
    return "Mật khẩu quá yếu, hãy chọn mật khẩu dài và khó đoán hơn.";
  if (code === "auth/network-request-failed") {
    return "Không kết nối được máy chủ. Kiểm tra lại mạng rồi thử lại.";
  }
  return firebaseAuthErrorMessage(error) ?? "Không thực hiện được. Vui lòng thử lại.";
}
